import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil} from 'yougile-util'
import {DataObjectId, Hub, PermissionRoleKey} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {UserAccess} from '@extensions/user-access'
import {getLicenseUserCount, getUserCount} from '../dashboard-utils'
import {EmailsInputItem, ValidType} from './emails-input'
import {ProjectsListItem, RoleType} from './types'

interface State {
  roleForNewProject: PermissionRoleKey
  wait: boolean
}

const initState: State = {
  roleForNewProject: 'worker',
  wait: false
}

export type InviteFrom = 'dashboard' | 'invite-employee' | 'manage-project'

export class CompanyInvitePopupStore {
  state: State = initState
  projects: ProjectsListItem[] = []
  emails: EmailsInputItem[] = []
  usersCount: number
  licenseCount: number
  inviteFrom: InviteFrom

  constructor(inviteFrom: InviteFrom) {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
    this.inviteFrom = inviteFrom
  }

  syncProjects(): void {
    const projects = Project.listProjects(
      globals.getCompanyId(),
      'not-archive',
      true
    )
      .sort(Project.projectOrderComparer)
      .filter(p => UserAccess.canChangeUserInProject(p))

    this.projects = projects.map((p, idx) => {
      return {
        id: p.id,
        title: p.title,
        enabled: idx === 0,
        roles: getRoles(p),
        defaultRole: 'worker'
      }
    })
  }

  syncProject(currentProject?: Hub): void {
    if (currentProject) {
      this.projects = [currentProject].map((p, idx) => {
        return {
          id: p.id,
          title: p.title,
          enabled: idx === 0,
          roles: getRoles(p),
          defaultRole: 'worker'
        }
      })
    }
  }

  syncCommon(): void {
    this.licenseCount = getLicenseUserCount()
    this.usersCount = getUserCount()
  }

  clearEmails(): void {
    this.emails = []
  }

  onDefaultRoleChanged(
    projectId: DataObjectId,
    value: PermissionRoleKey
  ): void {
    const p = this.getProject(projectId)
    if (p) {
      p.defaultRole = value
    }
  }

  setDefaultRoleForNewProject(value: PermissionRoleKey): void {
    this.state.roleForNewProject = value
  }

  onEnabledChanged(projectId: DataObjectId, value: boolean): void {
    const p = this.getProject(projectId)
    if (p) {
      p.enabled = value
    }
  }

  getProject(id: DataObjectId): ProjectsListItem | undefined {
    return this.projects.find(p => p.id === id) ?? undefined
  }

  getProjectsToInvite(): {projectId: DataObjectId; role: PermissionRoleKey}[] {
    return this.projects
      .filter(p => p.enabled)
      .map(p => ({projectId: p.id, role: p.defaultRole}))
  }

  setEmails(value: EmailsInputItem[]): void {
    this.emails = value

    this.licenseCount = getLicenseUserCount()
    this.usersCount = getUserCount()
  }

  hasLicenseLimit(): boolean {
    const usersCount =
      this.usersCount + this.emails.filter(i => i.valid === 'success').length
    return this.licenseCount < usersCount
  }

  needLicenseCount(): number {
    const usersCount =
      this.usersCount + this.emails.filter(i => i.valid === 'success').length
    return usersCount - this.licenseCount
  }

  get hasErrors(): boolean {
    return this.emails.some(i => i.valid === 'error')
  }

  get hasWarnings(): boolean {
    return this.emails.some(i => i.valid === 'warning')
  }

  canSubmit(): boolean {
    return !!this.emails.length && !this.hasErrors && !this.hasLicenseLimit()
  }

  validateEmail(email: string): ValidType {
    const box = Sync.getCurrentSandbox()
    const users = new Set(box.getAllUsers(false).map(u => u.email))
    if (users.has(email)) {
      return 'warning'
    }

    return 'success'
  }

  async onSubmit(): Promise<void> {
    this.state.wait = true

    const failedEmails: {email: string; error: string}[] = []
    const companyId = globals.getCompanyId()
    const projectsToInvite = this.getProjectsToInvite()

    for (const email of this.emails) {
      if (email.valid === 'success' || email.valid === 'warning') {
        const {error} = await UserAccess.invite(
          email.text,
          companyId,
          projectsToInvite
        )
        if (error) {
          failedEmails.push({email: email.text, error})
          logger.error('Sending error', email, error)
        }
      }
    }

    if (failedEmails.length === 0) {
      MessageBox.show(
        this.emails.length === 1
          ? Lang.msg.userInviteOk
          : Lang.msg.usersInviteOk(this.emails.length),
        'success'
      )
    } else {
      MessageBox.show(
        Lang.msg.permission.emailInviteFail +
          ': ' +
          failedEmails.map(f => f.email).join(', '),
        'danger'
      )
    }

    this.state.wait = false

    void Tracking.send(
      TrackingEvent.userInvitationPopupClickOnSendEmailInvitationsButton,
      {emailsCount: this.emails.length, inviteFrom: this.inviteFrom}
    )
  }
}

const [CompanyInvitePopupContext, useCompanyInvitePopupStore] = createUseStore(
  CompanyInvitePopupStore
)
export {CompanyInvitePopupContext, useCompanyInvitePopupStore}

export const getDefaultRoles = (): RoleType[] => {
  return [
    {
      role: 'admin',
      text: Lang.msg.permission.adminRole
    },
    {
      role: 'worker',
      text: Lang.msg.permission.workerRole
    },
    {
      role: 'observer',
      text: Lang.msg.permission.observerRole
    }
  ]
}

export const getRoles = (p: Hub): RoleType[] => {
  const customRoles: RoleType[] = ObjUtil.list(
    {...(p?.data?.roles ?? {})},
    (_id, v) => v
  )
    .reverse()
    .map(r => {
      return {
        role: r.key,
        text: r.name
      }
    })

  const roles = getDefaultRoles()

  return [...customRoles, ...roles]
}
